import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Package, Workflow, KanbanSquare, Layout, Mail, Tag, CheckCircle2, Zap,
  Loader2, Download, Eye, Target, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const TRIGGER_LABELS = {
  contact_created: "Contact created", tag_added: "Tag added",
  stage_changed: "Stage changed", form_submitted: "Form submitted",
};

export default function Snapshots() {
  const { activeSub, activeSubId, subAccounts } = useAuth();
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [installs, setInstalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    Promise.all([
      api.get("/snapshots"),
      api.get(`/snapshots/installs/all`),
    ]).then(([s, i]) => { setSnapshots(s.data); setInstalls(i.data); setLoading(false); });
  }, [activeSubId]);

  useEffect(() => { load(); }, [load]);

  const installedHere = (key) => installs.find((i) => i.snapshot_key === key && i.sub_account_id === activeSubId);
  const installsFor = (key) => installs.filter((i) => i.snapshot_key === key);

  const install = async (key) => {
    setInstalling(key);
    try {
      const { data } = await api.post(`/snapshots/${key}/install?sub_account_id=${activeSubId}`);
      const c = data.install.counts;
      toast.success(`Installed into ${activeSub?.name}: ${c.workflows} workflows, ${c.templates} templates, 1 pipeline, 1 funnel`);
      setPreview(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setInstalling(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Agency Library</p>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Snapshots</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          One-click, pre-built vertical packages — a funnel, pipeline, automations, and message templates that
          drop into <span className="text-foreground font-medium">{activeSub?.name}</span> and go live in minutes.
          Everything stays fully editable after import.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {snapshots.map((s) => {
          const here = installedHere(s.key);
          const all = installsFor(s.key);
          return (
            <Card key={s.key} className="p-6 bg-card border-border/70 flex flex-col" data-testid={`snapshot-card-${s.key}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-lg">{s.name}</h3>
                    <Badge variant="secondary" className="text-[10px]">v{s.version}</Badge>
                    {here && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />Installed</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{s.vertical}</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{s.description}</p>

              <div className="grid grid-cols-2 gap-3 mt-5">
                {[
                  { icon: Layout, label: `${s.funnel_pages.length}-step funnel`, c: "#6366F1" },
                  { icon: KanbanSquare, label: `${s.pipeline_stages.length}-stage pipeline`, c: "#8B5CF6" },
                  { icon: Workflow, label: `${s.workflows.length} automations`, c: "#06B6D4" },
                  { icon: Mail, label: `${s.template_count} templates`, c: "#10B981" },
                ].map((m) => (
                  <div key={m.label} className="flex items-center gap-2 text-sm">
                    <m.icon className="w-4 h-4" style={{ color: m.c }} />
                    <span className="text-muted-foreground">{m.label}</span>
                  </div>
                ))}
              </div>

              {all.length > 0 && (
                <p className="text-xs text-muted-foreground mt-4">
                  Installed in {all.length} sub-account{all.length === 1 ? "" : "s"}: {all.map((i) => i.sub_account_name).join(", ")}
                </p>
              )}

              <div className="flex gap-2 mt-6 pt-5 border-t border-border/60">
                <Button variant="outline" className="gap-1.5 flex-1" onClick={() => setPreview(s)} data-testid={`preview-snapshot-${s.key}`}>
                  <Eye className="w-4 h-4" /> Preview
                </Button>
                <Button className="gap-1.5 flex-1 font-semibold" onClick={() => install(s.key)} disabled={installing === s.key} data-testid={`install-snapshot-${s.key}`}>
                  {installing === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {here ? "Re-install" : "Install"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="snapshot-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />{preview.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-2">
                <p className="text-sm text-muted-foreground">{preview.description}</p>

                <Section icon={Layout} title="Funnel">
                  <div className="flex flex-wrap items-center gap-2">
                    {preview.funnel_pages.map((p, i) => (
                      <React.Fragment key={p.slug}>
                        <span className="text-xs px-2.5 py-1 rounded-md bg-secondary">{p.name}</span>
                        {i < preview.funnel_pages.length - 1 && <span className="text-muted-foreground">→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </Section>

                <Section icon={KanbanSquare} title="Pipeline stages">
                  <div className="flex flex-wrap gap-2">
                    {preview.pipeline_stages.map((st) => <Badge key={st} variant="secondary" className="text-[11px]">{st}</Badge>)}
                  </div>
                </Section>

                <Section icon={Target} title="Custom fields">
                  <div className="space-y-1.5">
                    {preview.custom_fields.map((f) => (
                      <div key={f.key} className="flex items-center gap-2 text-sm">
                        <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-medium">{f.label}</span>
                        <span className="text-xs text-muted-foreground">{f.options ? f.options.join(" · ") : "text"}</span>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section icon={Workflow} title={`${preview.workflows.length} automations`}>
                  <div className="space-y-2">
                    {preview.workflows.map((w) => (
                      <div key={w.name} className="flex items-center gap-2 text-sm p-2.5 rounded-lg bg-secondary/40">
                        <Zap className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-medium flex-1">{w.name}</span>
                        <Badge variant="secondary" className="text-[10px]">{TRIGGER_LABELS[w.trigger]}{w.trigger_config?.tag ? `: ${w.trigger_config.tag}` : ""}</Badge>
                        {w.goal && <Badge className="text-[10px] bg-violet-500/15 text-violet-300 border-0">goal</Badge>}
                        <span className="text-xs text-muted-foreground">{w.steps} steps</span>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>
              <div className="flex justify-end pt-2 border-t border-border/60">
                <Button className="gap-1.5 font-semibold" onClick={() => install(preview.key)} disabled={installing === preview.key} data-testid="preview-install-button">
                  {installing === preview.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Install into {activeSub?.name}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{title}</span>
      </div>
      {children}
    </div>
  );
}
